/**
 * Highcharts option factory for the Income-Statement Sankey card. Deliberately
 * a plain (non-stock) chart — it follows the same option-factory pattern as
 * valuationChartConfig.js (credits/legend off, HTML tooltip, a `responsive.rules`
 * block, hardcoded hex colours; the app has no theme/dark-mode system).
 *
 * Design:
 *  - ALL nodes have explicit `column` from the model (0-4). No fixpoint relaxation.
 *  - Column-to-side mapping is a direct lookup (col 0 → left, col 4 → right, else top).
 *  - Wider margins for more label room (140px each side).
 *  - Taller chart (650px) for comfortable vertical spacing.
 *  - Styled tooltip with background, border, shadow.
 *  - Slightly less curvy links (curveFactor 0.4).
 */

// Currency symbols for the reporting currencies FMP commonly returns; anything
// unmapped falls back to appending the ISO code (e.g. "1.20B SEK").
const CURRENCY_SYMBOLS = {
  USD: '$', EUR: '€', GBP: '£', JPY: '¥', CNY: '¥', HKD: 'HK$',
  CAD: 'C$', AUD: 'A$', CHF: 'CHF ', INR: '₹', KRW: '₩', BRL: 'R$',
};

/**
 * Formats a statement amount compactly (e.g. `$1.23B`, `€45.6M`, `1.20B SEK`)
 * for the given reporting `currency`. Mirrors chartConfig.js's
 * `formatCompactCurrency` magnitude tiers (T/B/M) and sign handling, but is
 * currency-aware since the Sankey shows figures in their filing currency.
 */
export const formatStatementAmount = (value, currency) => {
  if (value == null || Number.isNaN(value)) return '';
  const symbol = CURRENCY_SYMBOLS[currency] || '';
  // Unmapped currency → append the ISO code after the number instead.
  const suffix = symbol || !currency ? '' : ` ${currency}`;
  const abs = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  let magnitude;
  if (abs >= 1e12) magnitude = `${(abs / 1e12).toFixed(2)}T`;
  else if (abs >= 1e9) magnitude = `${(abs / 1e9).toFixed(2)}B`;
  else if (abs >= 1e6) magnitude = `${(abs / 1e6).toFixed(2)}M`;
  else magnitude = abs.toFixed(2);
  return `${sign}${symbol}${magnitude}${suffix}`;
};

// Semantic category table.
const CATEGORIES = [
  { key: 'revenue', label: 'Revenue', nodeColor: '#3B7EA1', linkColor: '#90BEE0', kinds: ['revenue'] },
  { key: 'profitIncome', label: 'Profit & Income', nodeColor: '#27AE60', linkColor: '#8AD0A3', kinds: ['profit', 'income'] },
  { key: 'cost', label: 'Costs', nodeColor: '#C0392B', linkColor: '#E99B9B', kinds: ['cost'] },
  { key: 'loss', label: 'Loss', nodeColor: '#8E44AD', linkColor: '#C39BD3', kinds: ['loss'] },
];

// kind -> category object
const CATEGORY_BY_KIND = {};
CATEGORIES.forEach((c) => c.kinds.forEach((k) => { CATEGORY_BY_KIND[k] = c; }));

// Derived lookups: node fill by kind, link tint by destination kind.
export const NODE_COLORS = {};
const LINK_COLORS = {};
Object.entries(CATEGORY_BY_KIND).forEach(([k, c]) => {
  NODE_COLORS[k] = c.nodeColor;
  LINK_COLORS[k] = c.linkColor;
});
// Passthrough: quiet gray connector, not a legend category.
LINK_COLORS.passthrough = '#DDDDDD';
// Token flows use the same quiet gray.
LINK_COLORS.token = '#DDDDDD';

// Link colour: by destination kind, except loss flows → purple, passthrough/token → gray.
const linkColorFor = (flow, toKind) => {
  if (flow === 'loss') return LINK_COLORS.loss;
  if (flow === 'passthrough' || flow === 'token') return LINK_COLORS.token;
  return LINK_COLORS[toKind] || LINK_COLORS.revenue;
};

// Label colour for a node — resolves from its kind.
const labelColorFor = (kind) => NODE_COLORS[kind] || '#2c3e50';

// Node bar geometry
const NODE_WIDTH = 12;
const SIDE_GAP = 12;
const TOP_LIFT = 38;
const MAX_COLUMN = 4;

// Per-node dataLabel placement from column position.
const SIDE_PLACEMENT = {
  left: { align: 'right', verticalAlign: 'middle', x: -(NODE_WIDTH + SIDE_GAP), y: 0 },
  right: { align: 'left', verticalAlign: 'middle', x: NODE_WIDTH + SIDE_GAP, y: 0 },
  top: { align: 'center', verticalAlign: 'top', x: 0, y: -TOP_LIFT },
};

const SIDE_TEXT_ALIGN = { left: 'right', right: 'left', top: 'center' };

const sideForColumn = (col) => {
  if (col === 0) return 'left';
  if (col === MAX_COLUMN) return 'right';
  return 'top';
};

const FONT_FAMILY = "'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";
const HALO = '0 0 3px #fff, 0 0 3px #fff, 0 0 4px #fff';

/**
 * Builds the full Sankey chart options for one period's model
 * (`buildIncomeSankeyModel`'s output) shown in reporting `currency`. Passed
 * wholesale to `HighchartsReact`'s `options` prop.
 */
export const getIncomeStatementSankeyOptions = (
  model,
  { currency, onNodeClick = null, onNodeOut = null } = {},
) => {
  // Build a node-id → kind lookup for link colouring.
  const kindById = {};
  model.nodes.forEach((n) => { kindById[n.id] = n.kind; });

  return {
    chart: {
      type: 'sankey',
      height: 650,
      marginTop: 50,
      marginLeft: 140,
      marginRight: 140,
      style: { fontFamily: FONT_FAMILY },
    },
    title: { text: undefined },
    credits: { enabled: false },
    legend: { enabled: false },
    tooltip: {
      useHTML: true,
      headerFormat: '',
      backgroundColor: 'rgba(255, 255, 255, 0.96)',
      borderColor: '#E0E0E0',
      borderRadius: 6,
      shadow: { color: 'rgba(0,0,0,0.06)', offsetX: 0, offsetY: 2, width: 8 },
      style: { fontSize: '12px', fontFamily: FONT_FAMILY },
      nodeFormatter() {
        const node = this.point || this;
        const custom = (node.options && node.options.custom) || {};
        const name = node.name;
        const v = custom.value != null ? custom.value : node.sum;
        const color = custom.labelColor || '#2c3e50';
        return `<b style="color:${color}">${name}</b><br/>`
          + `${formatStatementAmount(v, currency)}`;
      },
      pointFormatter() {
        const pct = this.options && this.options.custom ? this.options.custom.pct : null;
        const fromSum = this.fromNode && this.fromNode.sum;
        const ofParent = fromSum > 0 ? ((this.weight / fromSum) * 100) : null;
        const fromIsRevenue = this.fromNode && this.fromNode.name === 'Revenue';
        const parts = [];
        if (pct != null) parts.push(`${pct.toFixed(1)}% of revenue`);
        if (ofParent != null && !fromIsRevenue) parts.push(`${ofParent.toFixed(1)}% of ${this.fromNode.name}`);
        const pctText = parts.length > 0
          ? ` <span style="opacity:0.7">(${parts.join(' · ')})</span>` : '';
        return `<span style="color:#666">${this.fromNode.name} &rarr; ${this.toNode.name}</span><br/>`
          + `<b>${formatStatementAmount(this.weight, currency)}</b>${pctText}`;
      },
    },
    plotOptions: {
      sankey: {
        nodeWidth: NODE_WIDTH,
        nodePadding: 55,
        borderWidth: 0,
        borderRadius: 0,
        minLinkWidth: 4,
        curveFactor: 0.4,
        linkOpacity: 0.45,
        cursor: 'pointer',
        point: {
          events: {
            mouseOver() {
              if (this.isNode && onNodeClick) onNodeClick(this.id);
            },
            mouseOut() {
              if (this.isNode && onNodeOut) onNodeOut();
            },
          },
        },
        states: {
          hover: { linkOpacity: 0.85 },
          inactive: { linkOpacity: 0.12, opacity: 0.4 },
        },
        dataLabels: {
          enabled: true,
          useHTML: true,
          padding: 0,
          crop: false,
          overflow: 'allow',
          nodeFormatter() {
            const node = this.point || this;
            const custom = (node.options && node.options.custom) || {};
            const name = node.name;
            const v = custom.value != null ? custom.value : node.sum;
            const color = custom.labelColor || '#2c3e50';
            const textAlign = SIDE_TEXT_ALIGN[custom.side] || 'center';
            const rescaleGlyph = custom.rescaled ? '↕ ' : '';
            const nodeId = node.id || (node.options && node.options.id) || '';
            return `<div data-sankey-node="${nodeId}" style="text-align:${textAlign};line-height:1.3;white-space:nowrap;cursor:pointer">`
              + `<span style="color:${color};font-weight:600;font-size:12px;text-shadow:${HALO}">${rescaleGlyph}${name}</span><br/>`
              + `<span style="color:${color};font-weight:400;font-size:11px;opacity:0.82;text-shadow:${HALO}">${formatStatementAmount(v, currency)}</span>`
              + '</div>';
          },
          style: { fontSize: '12px', fontWeight: '400', textOutline: 'none' },
        },
      },
    },
    series: [
      {
        type: 'sankey',
        keys: ['from', 'to', 'weight'],
        showInLegend: false,
        data: model.links.map((l) => ({
          from: l.from,
          to: l.to,
          weight: l.weight,
          color: linkColorFor(l.flow, kindById[l.to]),
          custom: { pct: l.pct },
        })),
        nodes: model.nodes.map((n) => {
          const col = n.column != null ? n.column : 0;
          const side = sideForColumn(col);
          return {
            id: n.id,
            name: n.name,
            color: NODE_COLORS[n.kind] || NODE_COLORS.profit,
            column: col,
            custom: {
              value: n.value,
              labelColor: labelColorFor(n.kind),
              side,
              rescaled: !!n.rescaled,
            },
            dataLabels: SIDE_PLACEMENT[side],
          };
        }),
      },
    ],
    responsive: {
      rules: [{
        condition: { maxWidth: 560 },
        chartOptions: {
          chart: { height: 530, marginTop: 42, marginLeft: 84, marginRight: 84 },
          legend: { itemStyle: { fontSize: '9.5px' }, itemDistance: 12 },
          plotOptions: {
            sankey: {
              nodeWidth: 10,
              nodePadding: 40,
              dataLabels: { style: { fontSize: '9.5px' } },
            },
          },
        },
      }],
    },
  };
};

export default getIncomeStatementSankeyOptions;
